import { getTables } from '../../requests/documentDatas';

/**
 * Registre module-level des **tables du document** — celles auxquelles un bloc
 * Tableau peut se brancher (`tableName`, cf. `webTableRefresh.js`).
 *
 * Il vit **hors** du store Puck (invariant §4.3 du README) : le champ de la
 * sidebar le lit par `useSyncExternalStore`.
 *
 * Sans source, le statut vaut `unavailable` et le champ ne s'affiche pas du
 * tout : un `web_template` n'est rattaché à aucun document, donc à aucune
 * table. C'est le même arrangement que le catalogue de variables
 * (`variableCatalogRegistry.js`), dont ce module est le calque.
 *
 * Chargement **paresseux** : aucun appel API à l'ouverture de l'éditeur, la
 * liste n'est lue qu'à la sélection d'un bloc Tableau.
 */

const EMPTY_TABLES = [];

/**
 * Tables portées par la réponse, quelle que soit sa forme.
 *
 * Deux routes répondent à `<document>/tables`, et pas de la même façon : une
 * **opportunité** renvoie un tableau de noms, une **offre** renvoie un objet
 * `{ offer_tables, opportunities_table }` — ses propres tables, puis celles de
 * chaque opportunité rattachée, groupées.
 *
 * Une table d'opportunité rattachée ne se lit **pas** sur la route de l'offre :
 * chaque entrée porte donc la source qui la sert (`null` = le document ouvert),
 * et c'est cette source que le bloc mémorise à côté du nom de la table.
 *
 * @returns {{ name: string, source: {id: string, type: string}|null, group: string|null }[]}
 */
export function tableEntriesFromResponse(raw) {
  const value = raw?.tables;
  if (Array.isArray(value)) {
    return value.map((name) => ({ name, source: null, group: null }));
  }
  if (!value || typeof value !== 'object') return EMPTY_TABLES;

  const own = Array.isArray(value.offer_tables)
    ? value.offer_tables.map((name) => ({ name, source: null, group: null }))
    : [];

  const attached = Array.isArray(value.opportunities_table)
    ? value.opportunities_table.flatMap((opportunity) => {
      const id = opportunity?.opportunity_id;
      if (id == null) return [];
      return (opportunity.opportunity_tables || []).map((name) => ({
        name,
        source: { id: String(id), type: 'opportunity' },
        group: opportunity.opportunity_name || null,
      }));
    })
    : [];

  return [...own, ...attached];
}

let source = null;
let tables = EMPTY_TABLES;
/** Tables avec la source qui les sert — cf. `tableEntriesFromResponse`. */
let entries = EMPTY_TABLES;
/** Capabilities du backend (ex. bpu `additional_work`) : elles décident des
 *  options offertes par l'écran de configuration d'un tableau paramétrable. */
let capabilities = null;
/** Tables que le backend sait découper par entité (`splittable_tables`). */
let splittableTables = EMPTY_TABLES;
let status = 'idle';
let error = null;
let inFlight = null;
let snapshot = {
  tables: EMPTY_TABLES,
  entries: EMPTY_TABLES,
  capabilities: null,
  splittableTables: EMPTY_TABLES,
  status: 'idle',
  error: null,
};

const listeners = new Set();

function emit() {
  snapshot = { tables, entries, capabilities, splittableTables, status, error };
  listeners.forEach((listener) => listener());
}

/** Identité du document qui fournit les tables : `{ id, type }`. */
export function setTableCatalogSource(next) {
  const nextId = next?.id != null ? String(next.id) : null;
  const nextType = next?.type || null;
  if (source?.id === nextId && source?.type === nextType) return;
  source = nextId && nextType ? { id: nextId, type: nextType } : null;
  tables = EMPTY_TABLES;
  entries = EMPTY_TABLES;
  capabilities = null;
  splittableTables = EMPTY_TABLES;
  status = 'idle';
  error = null;
  inFlight = null;
  emit();
}

export function getTableCatalogSource() {
  return source;
}

/**
 * Les tables sont servies par un modèle web : leurs noms et leur forme
 * (catalogue de colonnes) existent, leurs lignes non — elles n'arrivent qu'à
 * la création du site.
 */
export function isTemplateTableCatalog() {
  return source?.type === 'web_template';
}

export function subscribeTableCatalog(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getTableCatalogSnapshot() {
  return snapshot;
}

/**
 * Charge la liste des tables (une seule requête en vol à la fois).
 *
 * L'échec reste **visible** — `status: 'error'` et une trace console : un
 * `.catch` silencieux ferait passer une liste vide pour « ce document n'a pas
 * de table ».
 *
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<string[]>}
 */
export function loadTableCatalog({ force = false } = {}) {
  if (!source) {
    if (status !== 'unavailable') {
      status = 'unavailable';
      error = null;
      emit();
    }
    return Promise.resolve(EMPTY_TABLES);
  }
  if (!force && status === 'ready') return Promise.resolve(tables);
  if (inFlight && !force) return inFlight;

  const requested = source;
  status = 'loading';
  error = null;
  emit();

  inFlight = getTables(requested.type, requested.id)
    .then((raw) => {
      // Requête d'un document précédent : son résultat ne doit pas s'installer.
      if (requested !== source) return tables;
      if (!raw) throw new Error('liste des tables indisponible');
      entries = tableEntriesFromResponse(raw);
      tables = entries.map((entry) => entry.name);
      capabilities = raw.bpu_capabilities || null;
      splittableTables = raw.splittable_tables || EMPTY_TABLES;
      status = 'ready';
      error = null;
      emit();
      return tables;
    })
    .catch((e) => {
      if (requested !== source) return tables;
      console.error('[webviewer] chargement des tables du document', requested, e);
      status = 'error';
      error = e;
      emit();
      return EMPTY_TABLES;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/** Remise à zéro complète — réservée aux tests. */
export function resetTableCatalogRegistry() {
  source = null;
  tables = EMPTY_TABLES;
  entries = EMPTY_TABLES;
  capabilities = null;
  splittableTables = EMPTY_TABLES;
  status = 'idle';
  error = null;
  inFlight = null;
  snapshot = {
    tables: EMPTY_TABLES,
    entries: EMPTY_TABLES,
    capabilities: null,
    splittableTables: EMPTY_TABLES,
    status: 'idle',
    error: null,
  };
  listeners.clear();
}
